import {
  BLOCK_KEY,
  getSelectionState,
  removeEntity,
} from '@src/state/document/document.helper'
import { captureError } from '@src/utils/errors'
import {
  ContentBlock,
  ContentState,
  convertFromRaw,
  EditorState,
  Modifier,
} from 'draft-js'
import {
  BaseEditorEntity,
  CardContent,
  EditorEntity,
  EditorEntityRange,
  EntityComments,
} from '../../../components/Editor/types'
import { CommentsMap } from '../actions'

export const getEntitiesFromEditor = (content: ContentState): EditorEntity[] => {
  const results: EditorEntity[] = []
  const mainBlock = content.getFirstBlock()
  let entityData: BaseEditorEntity
  mainBlock.findEntityRanges(
    char => {
      const entityKey: string | null = char.getEntity()

      if (!entityKey) {
        return false
      }

      const comments: EntityComments = content.getEntity(entityKey).getData().comments
      if (!comments) {
        return false
      }

      entityData = { comments }

      return true
    },
    (start, end) => {
      results.push({
        offset: start,
        end,
        ...entityData,
      })
    }
  )
  return results
}

export function insertSoftNewline(editorState: Draft.EditorState): Draft.EditorState {
  const contentState = Modifier.insertText(
    editorState.getCurrentContent(),
    editorState.getSelection(),
    '\n',
    editorState.getCurrentInlineStyle(),
    undefined
  )

  const newEditorState = EditorState.push(editorState, contentState, 'insert-characters')

  return EditorState.forceSelection(newEditorState, contentState.getSelectionAfter())
}

export function getEntityRange(
  contentState: ContentState,
  entityKey: string
): EditorEntityRange | undefined {
  const block = contentState.getFirstBlock()
  let entityRange
  block.findEntityRanges(
    char => char.getEntity() === entityKey,
    (offset, end) => {
      entityRange = {
        offset,
        end,
      }
    }
  )
  return entityRange
}

export const getCommentsFromRawEntity = (entity: Draft.EntityInstance) => {
  // handle loading old editorState from LS (prevously no data field)
  if (entity.getData().comments) {
    return getCommentsFromEntityData((entity.getData() as BaseEditorEntity).comments)
  }
  return []
}

export const getEntityComments = (entity: Draft.EntityInstance): EntityComments => {
  // Draft entity data is typed as any in DraftJS
  return (entity.getData() as BaseEditorEntity).comments
}

export const getCommentsFromEntityData = (entityComments: EntityComments) => {
  if (entityComments) {
    return Object.values(entityComments)
  }
  return []
}

export const findCommentRange = (
  contentState: ContentState,
  entityKeys: string[]
): EditorEntityRange | undefined => {
  return entityKeys.reduce<EditorEntityRange | undefined>((minMaxRange, entityKey) => {
    const range = getEntityRange(contentState, entityKey)

    if (!range) {
      return minMaxRange
    }
    if (!minMaxRange) {
      return range
    }

    minMaxRange.offset = Math.min(minMaxRange.offset, range.offset)
    minMaxRange.end = Math.max(minMaxRange.end, range.end)
    return minMaxRange
  }, undefined)
}

export const applyEntitiesToEditorState = (
  entities: EditorEntity[],
  editorState: Draft.EditorState
) => {
  return entities.reduce<Draft.EditorState>((acc, { offset, end, comments }) => {
    return applyEntityToEditorState({ comments, end, offset }, acc)
  }, editorState)
}

export const applyEntityToEditorState = (
  entity: EditorEntity,
  editorState: Draft.EditorState
) => {
  const contentState = editorState.getCurrentContent()
  const { comments, offset, end } = entity
  const contentStateWithEntity = contentState.createEntity('', 'MUTABLE', { comments })
  const key = contentStateWithEntity.getLastCreatedEntityKey()
  try {
    const newCS = Modifier.applyEntity(
      contentStateWithEntity,
      getSelectionState(offset, end),
      key
    )
    const newEditorState = EditorState.push(editorState, newCS, 'apply-entity')
    return newEditorState
  } catch (err) {
    err.entity = entity
    captureError(err)
    console.warn(
      'Warning: entity not applied to EditorState, ContentState is undefined for Card ',
      entity
    )
  }
  return editorState
}

export interface UpdateCommentOnEntity {
  contentState: ContentState
  entityKey: string
  updatedComment: CardContent
}
export const updateCommentOnEntity = ({
  contentState,
  entityKey,
  updatedComment,
}: UpdateCommentOnEntity) => {
  const entityData: BaseEditorEntity = contentState.getEntity(entityKey).getData()

  const newEntityData = {
    ...entityData,
    comments: {
      ...entityData.comments,
      [updatedComment.id]: updatedComment,
    },
  }

  return contentState.replaceEntityData(entityKey, newEntityData)
}

interface DeleteCommentFromEditor {
  id: string
  entityKeys: string[]
  contentState: ContentState
}
export function deleteCommentFromEditor({
  id: targetId,
  entityKeys: commentEKs,
  contentState,
}: DeleteCommentFromEditor) {
  return commentEKs.reduce((acc, entityKey) => {
    const { comments } = acc.getEntity(entityKey).getData()
    const { [targetId]: value, ...rest } = comments

    if (Object.keys(rest).length === 0) {
      return deleteEntityFromEditor(acc, entityKey)
    }

    return updateEntityInEditor(acc, rest, entityKey)
  }, contentState)
}

export function deleteEntityFromEditor(contentState: ContentState, entityKey: string) {
  const range = getEntityRange(contentState, entityKey)
  if (range) {
    const { offset, end } = range
    const selection = getSelectionState(offset, end)
    return removeEntity(contentState, selection)
  }
  return contentState
}

const updateEntityInEditor = (
  contentState: ContentState,
  comments: EntityComments,
  key: string
) => {
  const range = getEntityRange(contentState, key)

  if (range) {
    return contentState.replaceEntityData(key, { comments })
  }
  return contentState
}

export const getNewEditorStateFromText = (text: string): EditorState => {
  const rawContent = {
    blocks: [
      {
        depth: 1,
        entityRanges: [],
        inlineStyleRanges: [],
        key: BLOCK_KEY,
        text,
        type: 'unstyled',
      },
    ],
    entityMap: {},
  }
  return EditorState.createWithContent(convertFromRaw(rawContent))
}

// TODO: WTP-1665 - consider using separate key for document/draftjs
export function migrateEditorState({
  editorState,
  blockVersion,
}: {
  editorState: EditorState
  blockVersion: string
}) {
  const contentState = editorState.getCurrentContent()
  const blockKey = contentState.getFirstBlock().getKey()
  if (blockKey !== blockVersion) {
    return getNewEditorStateFromText(contentState.getPlainText())
  }
  return editorState
}

interface TReplaceText {
  contentState: ContentState
  selectedReplacement: string
  range: EditorEntityRange
}
export function replaceText({ contentState, selectedReplacement, range }: TReplaceText) {
  const { offset, end } = range
  const originalSelectionState = getSelectionState(offset, end)
  const csWithNewText = Modifier.replaceText(
    contentState,
    originalSelectionState,
    selectedReplacement
  )
  /* Trade off with the following code (option 2 is implemented): 
    1. We don't overwrite the new range: entities offsets inside this range remain same, 
        but the underlying text has changed, and so has the ranges.  Entity ranges will be
        meaningless.
    2. we clear all entities inside this range: this makes sense(?), since the underlying
        text and range has changed.  
  */
  const replacementSelectionState = getSelectionState(
    offset,
    offset + selectedReplacement.length
  )
  return removeEntity(csWithNewText, replacementSelectionState)
}

export function getIdToKeysMap(
  contentBlock: ContentBlock,
  entities: EditorEntity[]
): { [id: string]: string[] } {
  return entities.reduce<{ [id: string]: string[] }>((map, e) => {
    const { offset, comments } = e
    const ids = Object.keys(comments)
    ids.forEach(id => {
      if (!map[id]) {
        map[id] = []
      }
      map[id].push(contentBlock.getEntityAt(offset))
    })
    return map
  }, {})
}

export function migrateComments(cs: ContentState, comments: CommentsMap): CommentsMap {
  const entities = getEntitiesFromEditor(cs)
  const block = cs.getFirstBlock()
  const idToKeysMap = getIdToKeysMap(block, entities)
  return Object.values(comments)
    .map(c => ({ ...c, entityKeys: idToKeysMap[c.id] }))
    .reduce<CommentsMap>((map, c) => {
      map[c.id] = c
      return map
    }, {})
}

export function insertPastedText(
  text: string,
  editorState: EditorState
): Draft.EditorState {
  const contentState = Modifier.replaceText(
    editorState.getCurrentContent(),
    editorState.getSelection(),
    text,
    editorState.getCurrentInlineStyle(),
    undefined
  )
  const newEditorState = EditorState.push(editorState, contentState, 'insert-characters')

  return EditorState.forceSelection(newEditorState, contentState.getSelectionAfter())
}



// WEBPACK FOOTER //
// ./src/state/document/helpers/draftjs.helper.ts